import AddIcon from '@mui/icons-material/Add';
import { Alert, Box } from '@mui/material';
import { Button, SearchBar } from '@peritasai/ui-components';
import type { SurgeonEntity } from '@peritasai/repository';
import { useSurgeons } from '@peritasai/repository';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GridPaginationModel } from '@mui/x-data-grid-premium';
import { SurgeonDrawer } from './SurgeonDrawer';
import { DrawerMode } from '../../utils/drawer';
import { SurgeonsTable } from './SurgeonsTable';

export const SurgeonsPage = () => {
  const { t } = useTranslation(['periverse']);
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>(DrawerMode.Create);
  const [selectedSurgeon, setSelectedSurgeon] = useState<SurgeonEntity | null>(null);

  const { data, isLoading, isError } = useSurgeons({
    search: activeSearch || undefined,
    page: paginationModel.page,
    limit: paginationModel.pageSize,
  });

  const rows = data?.surgeons ?? [];
  const rowCount = data?.total ?? 0;

  const handleInputChange = (value: string) => {
    setSearchInput(value);
    if (!value) {
      setActiveSearch('');
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleSearch = (value: string) => {
    setActiveSearch(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClear = () => {
    setSearchInput('');
    setActiveSearch('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleRowClick = (row: SurgeonEntity) => {
    setSelectedSurgeon(row);
    setDrawerMode(DrawerMode.Edit);
    setDrawerOpen(true);
  };

  const handleAddClick = () => {
    setSelectedSurgeon(null);
    setDrawerMode(DrawerMode.Create);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
        <SearchBar
          value={searchInput}
          onChange={handleInputChange}
          onSearch={handleSearch}
          onClear={handleClear}
          placeholder={`${t('pages.entityManagement.searchPlaceholder', { entityName: t('pages.entityManagement.items.surgeons') })}...`}
          width={{ xs: '100%', sm: '45%' }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{ flexShrink: 0 }}
        >
          {t('common.add')}
        </Button>
      </Box>

      {isError && (
        <Alert severity="error">
          {t('pages.entityManagement.loadError')}
        </Alert>
      )}

      <SurgeonsTable
        rows={rows}
        rowCount={rowCount}
        isLoading={isLoading}
        hasSearch={activeSearch.length > 0}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPaginationModel(model)}
        onRowClick={handleRowClick}
      />

      <SurgeonDrawer
        open={drawerOpen}
        mode={drawerMode}
        selectedSurgeon={selectedSurgeon}
        onClose={handleDrawerClose}
      />
    </Box>
  );
};
